import { useCallback, useEffect, useState } from 'react'
import { 
  addDoc,
  collection, 
  doc, 
  getDocs,
  limit,
  onSnapshot, 
  orderBy,
  query, 
  serverTimestamp,
  where, 
  writeBatch,
} from 'firebase/firestore'
import { useAuth } from './useAuth.js'
import { db } from '../config/firebase.js'

// Configuration for cleanup - defined outside component to avoid dependency issues
const CLEANUP_CONFIG = {
  // Keep notification views for 6 months
  retentionMonths: 6,
  // Run cleanup every 7 days per user
  cleanupIntervalDays: 7,
  // Maximum views to keep per user (emergency brake)
  maxViewsPerUser: 1000,
}

/**
 * Notification Views Hook - Option 3 Implementation with TTL Cleanup
 * 
 * Manages read/unread state for all notification types using a centralized
 * notification-centric Firestore collection. This approach scales to handle
 * multiple notification types (access requests, device alerts, system notifications, etc.)
 * 
 * Features automatic cleanup of old notification views to prevent collection bloat.
 * 
 * Collection Structure:
 * notificationViews: {
 *   id: "auto-generated",
 *   notificationId: "notification-id",
 *   notificationType: "accessRequest" | "deviceAlert" | "systemNotification" | etc.,
 *   userId: "user-who-viewed",
 *   viewedAt: timestamp,
 *   deviceId: "optional-device-id",
 *   metadata: { optional-extra-data }
 * }
 * 
 * @returns {Object} Notification view management functions
 */
export function useNotificationViews() {
  const { user } = useAuth()
  const [viewedNotifications, setViewedNotifications] = useState(new Set())
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  /**
   * Clean up old notification views for the current user
   * Removes views older than the retention period
   * @returns {Promise<number>} Number of documents deleted
   */
  const cleanupOldViews = useCallback(async () => {
    if (!user?.uid) return 0

    try {
      const cutoffDate = new Date()
      cutoffDate.setMonth(cutoffDate.getMonth() - CLEANUP_CONFIG.retentionMonths)

      // Query old views for the current user
      const oldViewsQuery = query(
        collection(db, 'notificationViews'),
        where('userId', '==', user.uid),
        where('viewedAt', '<', cutoffDate),
        orderBy('viewedAt', 'asc'),
        limit(100), // Process in batches to avoid timeout
      )

      const snapshot = await getDocs(oldViewsQuery)
      
      if (snapshot.empty) return 0

      // Delete in batches
      const batch = writeBatch(db)
      let deleteCount = 0

      snapshot.docs.forEach((docSnapshot) => {
        batch.delete(docSnapshot.ref)
        deleteCount++
      })

      await batch.commit()
      
      return deleteCount
    } catch (err) {
      console.error('Error cleaning up old notification views:', err)
      return 0
    }
  }, [user?.uid])

  /**
   * Emergency cleanup if user has too many views
   * Keeps only the most recent views up to the limit
   * @returns {Promise<number>} Number of documents deleted
   */
  const emergencyCleanup = useCallback(async () => {
    if (!user?.uid) return 0

    try {
      // Count current views
      const countQuery = query(
        collection(db, 'notificationViews'),
        where('userId', '==', user.uid),
      )
      
      const countSnapshot = await getDocs(countQuery)
      const currentCount = countSnapshot.size

      if (currentCount <= CLEANUP_CONFIG.maxViewsPerUser) return 0

      // Get oldest views to delete
      const excessCount = currentCount - CLEANUP_CONFIG.maxViewsPerUser
      const oldestViewsQuery = query(
        collection(db, 'notificationViews'),
        where('userId', '==', user.uid),
        orderBy('viewedAt', 'asc'),
        limit(excessCount),
      )

      const snapshot = await getDocs(oldestViewsQuery)
      
      if (snapshot.empty) return 0

      // Delete in batches
      const batch = writeBatch(db)
      let deleteCount = 0

      snapshot.docs.forEach((docSnapshot) => {
        batch.delete(docSnapshot.ref)
        deleteCount++
      })

      await batch.commit()
      
      console.warn(`Emergency cleanup: deleted ${deleteCount} notification views (user had ${currentCount} total)`)
      return deleteCount
    } catch (err) {
      console.error('Error in emergency cleanup:', err)
      return 0
    }
  }, [user?.uid])

  /**
   * Check if cleanup should run and execute if needed
   * Runs cleanup based on the configured interval
   */
  const checkAndRunCleanup = useCallback(async () => {
    if (!user?.uid) return

    try {
      // Check localStorage for last cleanup time
      const lastCleanupKey = `notificationViews_lastCleanup_${user.uid}`
      const lastCleanup = localStorage.getItem(lastCleanupKey)
      const now = Date.now()
      
      const shouldRunCleanup = !lastCleanup || 
        (now - parseInt(lastCleanup)) > (CLEANUP_CONFIG.cleanupIntervalDays * 24 * 60 * 60 * 1000)

      if (shouldRunCleanup) {
        // Run both cleanup functions
        const [oldDeleted, emergencyDeleted] = await Promise.all([
          cleanupOldViews(),
          emergencyCleanup(),
        ])

        // Update last cleanup time
        localStorage.setItem(lastCleanupKey, now.toString())
        
        if (oldDeleted > 0 || emergencyDeleted > 0) {
          console.warn(`Notification cleanup: ${oldDeleted + emergencyDeleted} old views removed`)
        }
      }
    } catch (err) {
      console.error('Error in cleanup check:', err)
    }
  }, [user?.uid, cleanupOldViews, emergencyCleanup])

  /**
   * Load viewed notifications for the current user
   * Also triggers cleanup check on load
   */
  useEffect(() => {
    if (!user?.uid) {
      setViewedNotifications(new Set())
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    const viewsQuery = query(
      collection(db, 'notificationViews'),
      where('userId', '==', user.uid),
    )

    const unsubscribe = onSnapshot(
      viewsQuery,
      (snapshot) => {
        try {
          const viewedIds = new Set()
          snapshot.docs.forEach(doc => {
            const data = doc.data()
            // Create a composite key for notification + type for uniqueness
            const viewKey = `${data.notificationType}:${data.notificationId}`
            viewedIds.add(viewKey)
          })
          
          setViewedNotifications(viewedIds)
          setIsLoading(false)
        } catch (err) {
          console.error('Error loading notification views:', err)
          setError(err)
          setIsLoading(false)
        }
      },
      (err) => {
        console.error('Error listening to notification views:', err)
        setError(err)
        setIsLoading(false)
      },
    )

    // Trigger cleanup check after loading views
    checkAndRunCleanup()

    return () => unsubscribe()
  }, [user?.uid, checkAndRunCleanup])

  /**
   * Check if a notification has been viewed by the current user
   * @param {string} notificationId - The notification ID
   * @param {string} notificationType - The type of notification
   * @returns {boolean} True if viewed, false if unread
   */
  const isNotificationViewed = useCallback((notificationId, notificationType) => {
    if (!notificationId || !notificationType) return false
    const viewKey = `${notificationType}:${notificationId}`
    return viewedNotifications.has(viewKey)
  }, [viewedNotifications])

  /**
   * Mark a notification as viewed
   * @param {string} notificationId - The notification ID
   * @param {string} notificationType - The type of notification
   * @param {Object} options - Additional options
   * @param {string} options.deviceId - Optional device ID for device-specific notifications
   * @param {Object} options.metadata - Optional metadata to store
   * @returns {Promise<void>}
   */
  const markNotificationAsViewed = useCallback(async (notificationId, notificationType, options = {}) => {
    if (!user?.uid || !notificationId || !notificationType) {
      console.warn('Cannot mark notification as viewed: missing user, notificationId, or notificationType')
      return
    }

    const viewKey = `${notificationType}:${notificationId}`
    
    // If already viewed, skip the database write
    if (viewedNotifications.has(viewKey)) {
      return
    }

    try {
      const viewData = {
        notificationId,
        notificationType,
        userId: user.uid,
        viewedAt: serverTimestamp(),
        ...(options.deviceId && { deviceId: options.deviceId }),
        ...(options.metadata && { metadata: options.metadata }),
      }

      await addDoc(collection(db, 'notificationViews'), viewData)
      
      // Optimistically update local state
      setViewedNotifications(prev => new Set([...prev, viewKey]))
      
    } catch (err) {
      console.error('Error marking notification as viewed:', err)
      throw err
    }
  }, [user?.uid, viewedNotifications])

  /**
   * Mark multiple notifications as viewed (batch operation)
   * @param {Array} notifications - Array of {notificationId, notificationType, deviceId?, metadata?}
   * @returns {Promise<void>}
   */
  const markMultipleNotificationsAsViewed = useCallback(async (notifications) => {
    if (!user?.uid || !notifications?.length) {
      console.warn('Cannot mark notifications as viewed: missing user or notifications')
      return
    }

    try {
      const batch = writeBatch(db)
      const newViewKeys = new Set()

      notifications.forEach(({ notificationId, notificationType, deviceId, metadata }) => {
        if (!notificationId || !notificationType) return

        const viewKey = `${notificationType}:${notificationId}`
        
        // Skip if already viewed
        if (viewedNotifications.has(viewKey)) return

        const viewRef = doc(collection(db, 'notificationViews'))
        const viewData = {
          notificationId,
          notificationType,
          userId: user.uid,
          viewedAt: serverTimestamp(),
          ...(deviceId && { deviceId }),
          ...(metadata && { metadata }),
        }

        batch.set(viewRef, viewData)
        newViewKeys.add(viewKey)
      })

      if (newViewKeys.size > 0) {
        await batch.commit()
        
        // Optimistically update local state
        setViewedNotifications(prev => new Set([...prev, ...newViewKeys]))
      }
      
    } catch (err) {
      console.error('Error marking multiple notifications as viewed:', err)
      throw err
    }
  }, [user?.uid, viewedNotifications])

  /**
   * Filter notifications to only include unread ones
   * @param {Array} notifications - Array of notifications with id property
   * @param {string} notificationType - The type of notifications
   * @returns {Array} Filtered array of unread notifications
   */
  const filterUnreadNotifications = useCallback((notifications, notificationType) => {
    if (!notifications || !notificationType) return []
    
    return notifications.filter(notification => 
      !isNotificationViewed(notification.id, notificationType),
    )
  }, [isNotificationViewed])

  /**
   * Get unread count for specific notification type
   * @param {Array} notifications - Array of notifications
   * @param {string} notificationType - The type of notifications
   * @returns {number} Count of unread notifications
   */
  const getUnreadCount = useCallback((notifications, notificationType) => {
    return filterUnreadNotifications(notifications, notificationType).length
  }, [filterUnreadNotifications])

  /**
   * Mark all notifications of a specific type as viewed
   * @param {Array} notifications - Array of notifications to mark as viewed
   * @param {string} notificationType - The type of notifications
   * @param {string} deviceId - Optional device ID for device-specific notifications
   * @returns {Promise<void>}
   */
  const markAllNotificationsAsViewed = useCallback(async (notifications, notificationType, deviceId = null) => {
    if (!notifications?.length || !notificationType) return

    const notificationsToMark = notifications.map(notification => ({
      notificationId: notification.id,
      notificationType,
      ...(deviceId && { deviceId }),
    }))

    await markMultipleNotificationsAsViewed(notificationsToMark)
  }, [markMultipleNotificationsAsViewed])

  return {
    // State
    isLoading,
    error,
    viewedNotifications,

    // Core functions
    isNotificationViewed,
    markNotificationAsViewed,
    markMultipleNotificationsAsViewed,

    // Utility functions
    filterUnreadNotifications,
    getUnreadCount,
    markAllNotificationsAsViewed,

    // Cleanup functions (for admin/maintenance use)
    cleanupOldViews,
    emergencyCleanup,
    checkAndRunCleanup,
  }
}

/**
 * Notification type constants for consistency
 */
export const NOTIFICATION_TYPES = {
  ACCESS_REQUEST: 'accessRequest',
  DEVICE_ALERT: 'deviceAlert',
  INVITATION: 'invitation',
  SYSTEM_NOTIFICATION: 'systemNotification',
  MAINTENANCE_ALERT: 'maintenanceAlert',
  FIRMWARE_UPDATE: 'firmwareUpdate',
  BILLING_ALERT: 'billingAlert',
}
